import { ArrowRight, GraduationCap, Award, BookOpen, Users } from "lucide-react";
import { DR_PHOTO } from "@/lib/assets";

const timeline = [
  { year: "PhD", event: "Restorative Dentistry", place: "University of Manchester — Prosthetic Design & Biomechanics" },
  { year: "MFDS", event: "Member, Faculty of Dental Surgery", place: "RCS England & RCS Edinburgh" },
  { year: "FFD", event: "Fellow, Faculty of Dentistry", place: "Royal College of Surgeons Ireland" },
  { year: "FDS", event: "Fellow in Dental Surgery", place: "RCPS Glasgow" },
  { year: "FHEA", event: "Fellow, Higher Education Academy", place: "UK" },
  { year: "GDC", event: "Specialist Register", place: "Restorative Dentistry · Prosthodontics · Periodontics · Endodontics" },
];

const specialisms = [
  "Dental Implants — Single to Full-Arch",
  "Full-Arch Rehabilitation",
  "Complex Restorative Dentistry",
  "Prosthodontics",
  "Periodontics",
  "Endodontics",
];

const roles = [
  { icon: <Award size={18} strokeWidth={1.5} />, role: "Clinical Director", org: "Specialist Dental London" },
  { icon: <GraduationCap size={18} strokeWidth={1.5} />, role: "Founder & Course Director", org: "London Dental Academy" },
  { icon: <BookOpen size={18} strokeWidth={1.5} />, role: "Senior Academic", org: "King's College London" },
  { icon: <Users size={18} strokeWidth={1.5} />, role: "International Lecturer", org: "Implant Rehabilitation & Maxillofacial Reconstruction" },
];

export default function AboutPage({ onBook }) {
  return (
    <main
      data-testid="about-page"
      style={{ background: '#FAFAF9', paddingTop: '64px' }}
    >
      {/* Hero */}
      <section
        className="py-24 md:py-32"
        style={{ background: '#1C1917' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-px" style={{ background: '#D4AF37' }} />
                <span className="font-outfit text-xs tracking-[0.25em] uppercase" style={{ color: '#D4AF37' }}>
                  About
                </span>
              </div>
              <h1
                className="font-cormorant font-light leading-none mb-4"
                style={{ fontSize: 'clamp(2.8rem, 6vw, 5rem)', color: '#FDF8ED' }}
              >
                Dr. Shihab<br />Romeed
              </h1>
              <p
                className="font-outfit text-sm tracking-wide uppercase mb-8"
                style={{ color: '#D4AF37' }}
              >
                Consultant in Restorative Dentistry &middot; Principal Implant Dentist
              </p>
              <button
                data-testid="about-page-book-btn"
                onClick={() => onBook(null)}
                className="flex items-center gap-3 font-outfit text-sm font-medium px-7 py-3 transition-all duration-200 hover:opacity-90 active:scale-95"
                style={{ background: '#D4AF37', color: '#16110F' }}
              >
                Book an Appointment
                <ArrowRight size={15} />
              </button>
            </div>
            <div className="lg:col-span-4 relative">
              <div
                className="absolute -top-3 -left-3 w-full h-full"
                style={{ border: '1px solid #3A2E28' }}
              />
              <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                <img
                  src={DR_PHOTO}
                  alt="Dr. Shihab Romeed"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ background: 'linear-gradient(90deg, #D4AF37, #1CB0C6)' }}
              />
            </div>
            <div className="lg:col-span-3 space-y-6">
              {[
                { num: '25+', label: 'Years Clinical Experience' },
                { num: '4', label: 'Royal College Fellowships' },
                { num: 'PhD', label: 'Restorative Dentistry' },
              ].map(s => (
                <div key={s.label} style={{ borderLeft: '2px solid #D4AF37', paddingLeft: '16px' }}>
                  <span className="font-cormorant text-4xl font-light block" style={{ color: '#FDF8ED' }}>
                    {s.num}
                  </span>
                  <span className="font-outfit text-xs tracking-wider uppercase" style={{ color: '#A89886' }}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      <section className="py-24 md:py-28" style={{ background: '#FAFAF9' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-px" style={{ background: '#D4AF37' }} />
                <span className="font-outfit text-xs tracking-[0.25em] uppercase" style={{ color: '#78716C' }}>
                  Biography
                </span>
              </div>
              <div className="space-y-5">
                <p className="font-outfit text-base leading-relaxed" style={{ color: '#1C1917' }}>
                  Dr. Shihab Romeed is Clinical Director and Principal Implant Dentist at Specialist Dental London, with more than 25 years of experience in advanced restorative and implant dentistry. As a Consultant in Restorative Dentistry, he leads a multidisciplinary team focused on dental implants, full-mouth rehabilitation, and complex restorative treatment.
                </p>
                <p className="font-outfit text-sm leading-relaxed" style={{ color: '#78716C' }}>
                  He is a UK-trained consultant with specialist registration in Restorative Dentistry, Prosthodontics, Periodontics, and Endodontics with the General Dental Council. He completed a PhD in Restorative Dentistry at the University of Manchester, focused on prosthetic design and biomechanics, followed by higher specialist training at King's College London.
                </p>
                <p className="font-outfit text-sm leading-relaxed" style={{ color: '#78716C' }}>
                  His postgraduate qualifications include MFDS RCS (Eng & Ed), FFD RCSI, FDS RCPS (Glasg), PGDip, and FHEA — fellowships and diplomas from all four Royal Surgical Colleges in the UK and Ireland.
                </p>
                <p className="font-outfit text-sm leading-relaxed" style={{ color: '#78716C' }}>
                  Clinically, Dr. Romeed focuses on implant dentistry — from single-tooth replacement to full-arch reconstruction, including complex cases with bone loss or previous implant failure. His patient-first approach combines precise planning with advanced surgical technique to restore both function and confidence.
                </p>
                <p className="font-outfit text-sm leading-relaxed" style={{ color: '#78716C' }}>
                  Alongside his clinical work, he is founder of London Dental Academy and has held senior academic roles at King's College London. He lectures internationally on implant rehabilitation and maxillofacial reconstruction.
                </p>
              </div>
            </div>

            <div>
              {/* Roles */}
              <div className="mb-12">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-px" style={{ background: '#D4AF37' }} />
                  <span className="font-outfit text-xs tracking-[0.25em] uppercase" style={{ color: '#78716C' }}>
                    Roles
                  </span>
                </div>
                <div className="space-y-4">
                  {roles.map(r => (
                    <div
                      key={r.role}
                      className="flex items-start gap-4 p-4"
                      style={{ border: '1px solid #E7E5E4', background: '#FFFFFF' }}
                    >
                      <div className="mt-0.5" style={{ color: '#D4AF37' }}>{r.icon}</div>
                      <div>
                        <p className="font-outfit text-sm font-medium" style={{ color: '#1C1917' }}>{r.role}</p>
                        <p className="font-outfit text-xs" style={{ color: '#78716C' }}>{r.org}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specialisms */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-px" style={{ background: '#1CB0C6' }} />
                  <span className="font-outfit text-xs tracking-[0.25em] uppercase" style={{ color: '#78716C' }}>
                    Specialisms
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {specialisms.map(s => (
                    <div
                      key={s}
                      className="font-outfit text-sm px-4 py-3"
                      style={{ border: '1px solid #E7E5E4', color: '#1C1917', background: '#FFFFFF' }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications timeline */}
      <section className="py-24" style={{ background: '#1C1917' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-14">
            <div className="divider-gold mx-auto mb-4" />
            <h2
              className="font-cormorant font-light"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#FDF8ED' }}
            >
              Qualifications & Registrations
            </h2>
          </div>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <div
                key={item.year}
                data-testid={`qualification-item-${i}`}
                className="flex items-start gap-8 py-6"
                style={{ borderBottom: i < timeline.length - 1 ? '1px solid #3A2E28' : 'none' }}
              >
                <div
                  className="shrink-0 w-16 text-center py-1"
                  style={{ border: '1px solid #D4AF37' }}
                >
                  <span className="font-outfit text-xs font-semibold tracking-wider" style={{ color: '#D4AF37' }}>
                    {item.year}
                  </span>
                </div>
                <div>
                  <h4 className="font-cormorant text-lg font-medium mb-0.5" style={{ color: '#FDF8ED' }}>
                    {item.event}
                  </h4>
                  <p className="font-outfit text-sm" style={{ color: '#A89886' }}>
                    {item.place}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20" style={{ background: '#FAFAF9' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <h2
            className="font-cormorant font-light mb-4"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#1C1917' }}
          >
            Book a consultation with Dr. Romeed
          </h2>
          <p className="font-outfit text-sm leading-relaxed mb-8 max-w-lg mx-auto" style={{ color: '#78716C' }}>
            Whether you're a patient seeking specialist care or a clinician looking to advance your practice, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="about-page-patient-book-btn"
              onClick={() => onBook('patient')}
              className="flex items-center justify-center gap-2 font-outfit text-sm font-medium px-8 py-4 transition-all duration-200 hover:opacity-90 active:scale-95"
              style={{ background: '#D4AF37', color: '#16110F' }}
            >
              Patient Consultation <ArrowRight size={15} />
            </button>
            <button
              data-testid="about-page-professional-book-btn"
              onClick={() => onBook('professional')}
              className="flex items-center justify-center gap-2 font-outfit text-sm font-medium px-8 py-4 border transition-all duration-200 hover:border-stone-800 active:scale-95"
              style={{ border: '1px solid #E7E5E4', color: '#1C1917', background: 'transparent' }}
            >
              Academy Enquiry <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
